/**
 * API client for the FastAPI backend.
 * All requests include credentials (session cookie).
 * Base URL is relative — React app is served from the same origin as the API.
 */

const BASE = "";  // same origin

async function request(method, path, body) {
  const opts = {
    method,
    credentials: "include",
    headers: {},
  };
  if (body !== undefined) {
    opts.headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  const res = await fetch(`${BASE}/api${path}`, opts);
  if (!res.ok) {
    let detail = res.statusText;
    try { detail = (await res.json()).detail || detail; } catch {}
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

const get = (path) => request("GET", path);
const post = (path, body) => request("POST", path, body);
const patch = (path, body) => request("PATCH", path, body);
const del = (path) => request("DELETE", path);

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
export const authApi = {
  login: (password) => post("/auth/login", { password }),
  logout: () => post("/auth/logout"),
  check: () => get("/auth/check"),
};

// ---------------------------------------------------------------------------
// Agents
// ---------------------------------------------------------------------------
export const agentsApi = {
  list: () => get("/agents"),
  get: (id) => get(`/agents/${id}`),
  create: (body) => post("/agents", body),
  patchName: (id, name) => patch(`/agents/${id}/name`, { name }),
  pause: (id) => post(`/agents/${id}/pause`),
  resume: (id) => post(`/agents/${id}/resume`),
  saveLimits: (id, body) => post(`/agents/${id}/limits`, body),
  savePersona: (id, body) => post(`/agents/${id}/persona`, body),
  saveDiscoverOffers: (id, offers) => post(`/agents/${id}/discover-offers`, { offers }),
  runTick: (id, tickType) => post(`/agents/${id}/run-tick`, { tickType }),
  refreshSession: (id, liAtCookie, jsessionid) => post(`/agents/${id}/session-cookie`, { liAtCookie, jsessionid }),
  delete: (id) => del(`/agents/${id}`),
};

// ---------------------------------------------------------------------------
// Contacts
// ---------------------------------------------------------------------------
export const contactsApi = {
  list: (agentId, params = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", params.page);
    if (params.statusFilter) qs.set("status_filter", params.statusFilter);
    if (params.offerFilter) qs.set("offer_filter", params.offerFilter);
    return get(`/agents/${agentId}/contacts?${qs}`);
  },
  manualAdd: (agentId, urls, offerId, campaignType) =>
    post(`/agents/${agentId}/contacts/manual-add`, { urls, offerId, campaignType }),
};

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------
export const templatesApi = {
  get: (agentId) => get(`/agents/${agentId}/templates`),
  save: (agentId, overrides) => post(`/agents/${agentId}/templates`, { overrides }),
};

// ---------------------------------------------------------------------------
// Inbox
// ---------------------------------------------------------------------------
export const inboxApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.agentId) qs.set("agent_id", params.agentId);
    if (params.search) qs.set("search", params.search);
    return get(`/inbox?${qs}`);
  },
  threadByChat: (chatId, agentId, attendeeId) => get(`/inbox/chat/${encodeURIComponent(chatId)}?agent_id=${agentId}${attendeeId ? `&attendee_id=${encodeURIComponent(attendeeId)}` : ""}`),
  replyByChat: (chatId, agentId, text) => post(`/inbox/chat/${encodeURIComponent(chatId)}/reply?agent_id=${agentId}`, { text }),
  suggestReply: (chatId, agentId, attendeeId) => post(`/inbox/chat/${encodeURIComponent(chatId)}/suggest-reply?agent_id=${agentId}${attendeeId ? `&attendee_id=${encodeURIComponent(attendeeId)}` : ""}`, {}),
  thread: (contactId) => get(`/inbox/${contactId}`),
  reply: (contactId, text) => post(`/inbox/${contactId}/reply`, { text }),
};

// ---------------------------------------------------------------------------
// Blitz
// ---------------------------------------------------------------------------
export const blitzApi = {
  get: () => get("/blitz"),
  masterToggle: () => post("/blitz/master-toggle"),
  toggleAgent: (id) => post(`/blitz/${id}/toggle`),
  setLimit: (id, dailyLimit) => request("PATCH", `/blitz/${id}/limit`, { dailyLimit }),
  run: (id) => post(`/blitz/${id}/run`),
};

// ---------------------------------------------------------------------------
// Discovery
// ---------------------------------------------------------------------------
export const discoverApi = {
  get: () => get("/discover"),
  run: (id) => post(`/discover/${id}/run`),
  status: (id) => get(`/discover/${id}/status`),
};

// ---------------------------------------------------------------------------
// Posts
// ---------------------------------------------------------------------------
export const postsApi = {
  list: () => get("/posts"),
  create: (body) => post("/posts", body),
  update: (id, body) => patch(`/posts/${id}`, body),
  remove: (id) => del(`/posts/${id}`),
  generateCaption: (id, body) => post(`/posts/${id}/generate-caption`, body),
  uploadMedia: async (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await fetch(`/api/posts/${id}/upload-media`, { method: "POST", credentials: "include", body: fd });
    if (!r.ok) { let d = r.statusText; try { d = (await r.json()).detail || d; } catch {} throw new Error(d); }
    return r.json();
  },
  savePersona: (agentId, persona, displayName) => post(`/agents/${agentId}/persona`, { persona, displayName }),
};

// ---------------------------------------------------------------------------
// Knowledge Base
// ---------------------------------------------------------------------------
export const docsApi = {
  list: () => get("/documents"),
  upload: async (file, category = "general") => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await fetch(`/api/documents/upload?category=${encodeURIComponent(category)}`, {
      method: "POST", credentials: "include", body: fd,
    });
    if (!r.ok) { let d = r.statusText; try { d = (await r.json()).detail || d; } catch {} throw new Error(d); }
    return r.json();
  },
  remove: (id) => del(`/documents/${id}`),
};

// ---------------------------------------------------------------------------
// Agent campaigns
// ---------------------------------------------------------------------------
export const campaignsApi = {
  get: (agentId) => get(`/agents/${agentId}/campaigns`),
  save: (agentId, config, allocation) => post(`/agents/${agentId}/campaigns`, { config, allocation }),
};

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------
export const settingsApi = {
  get: () => get("/settings"),
  save: (updates) => post("/settings", { updates }),
  restart: () => post("/settings/restart"),
};

// ---------------------------------------------------------------------------
// Unipile accounts
// ---------------------------------------------------------------------------
export const unipileApi = {
  accounts: () => get("/unipile-accounts"),
};
